import { Spin, Reset } from "assets/icons/Common";
import { Col, IconButtonInverted, Row } from "components/primitives";
import { lerp } from "components/utils";
import { set } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";
import useSound from "use-sound";

import { Wheel } from "./components/Wheel";
import { useRandomSegmentGenerator } from "./RandomSegmentGenerator";

export interface InteractiveSpinnerState {
  winningSegmentIndex: number | undefined;
  angleCurrent: number;
  winningSegmentIndexes: number[];
  remainingSegmentIndexes: number[];
}

const segmentStartColor = "#A1C3D1";
const disableSegmentColor = "#EBE9EB";
const segmentColours = [
  "#B4ADE9",
  "#80A0B6",
  "#FBBAD2",
  "#FF9090",
  "#FEE9A5",
  "#7FDFDB",
  "#FFB4A0",
  "#FF6F61",
];
export const startAngle = 0 * (Math.PI / 180);

let nextIndex = -1;
export const InteractiveSpinner: WidgetFC<InteractiveSpinnerState> = ({
  widgetId,
  state: { winningSegmentIndex, angleCurrent, winningSegmentIndexes, remainingSegmentIndexes },
  setState,
  settings,
  pixelPosition,
}) => {
  const dimens = pixelPosition.width ? pixelPosition.width : 500;
  const [mustSpin, setMustSpin] = useState(false);

  const widgetIdRef = useRef<string | null>(null);

  const { nameList, sound: shouldPlaySound, selectSound, disableWinners } = settings;

  const selectedSound = require("assets/sounds/" + (selectSound || "yoo-hoo.mp3"));
  const [play] = useSound(selectedSound);

  const segments: string[] =
    nameList?.split("\n").filter((lineItem: string) => Boolean(lineItem)) || [];

  const [winner, setWinner] = useState<string>(
    winningSegmentIndex ? segments[winningSegmentIndex] : "",
  );
  const [winnerIndex, setWinnerIndex] = useState<number>(
    winningSegmentIndex ? winningSegmentIndex : -1,
  );

  const { getNext } = useRandomSegmentGenerator(
    remainingSegmentIndexes.length ? remainingSegmentIndexes.length - 1 : segments.length - 1,
    !disableWinners,
    remainingSegmentIndexes,
  );

  const safeGetColourSegment = (index: number) => {
    if (index === 0) {
      return segmentStartColor;
    } else {
      return segmentColours[(index - 1) % segmentColours.length];
    }
  };

  const getFontSize = (value: string, minFont: number = 4, maxFont: number = 6) => {
    const minWordSize = 9;
    const maxWordSize = 15;
    const wordSize = Math.max((value?.length - minWordSize) / (maxWordSize - minWordSize), 0);
    return lerp(maxFont, minFont, wordSize);
  };

  const data = useMemo(() => {
    const canvasSize = pixelPosition.width!;
    const mapOptions = segments?.map((item, index) => {
      const value: string = segments[index];
      const fontSizeEm =
        getFontSize(value, lerp(4, 2, segments.length / 35), lerp(6, 2.5, segments.length / 35)) *
        0.0006 *
        canvasSize;

      return {
        option: item,
        style: {
          backgroundColor: winningSegmentIndexes.includes(index)
            ? disableSegmentColor
            : safeGetColourSegment(index),
          textColor: "black",
          fontSize: fontSizeEm,
          fontWeight: "bold",
        },
        isWinner: !mustSpin && nextIndex === index,
      };
    });
    return mapOptions;
  }, [segments, pixelPosition.width, mustSpin]);

  useEffect(() => {
    // Only reset if `disableWinners` changes and `widgetId` hasn't changed
    if (widgetIdRef.current === widgetId) {
      reset();
    }
    // Update the `widgetIdRef` to the current `widgetId` after the effect runs
    widgetIdRef.current = widgetId;
  }, [disableWinners, widgetId]);

  useEffect(() => {
    // Account for the difference between what we consider 0 degrees and what
    // the canvas considers it.
    if (!mustSpin && winningSegmentIndex) {
      setState({ winningSegmentIndex: winningSegmentIndex, angleCurrent });
      setWinner(segments[winningSegmentIndex]);
    }
  }, [mustSpin]);

  const spin = () => {
    nextIndex = -1;
    const nextObj = getNext();
    nextIndex = nextObj.next;
    setMustSpin(true);
    setState({ winningSegmentIndex: nextIndex, remainingSegmentIndexes: nextObj.numbers });
  };

  const reset = () => {
    nextIndex = -1;
    setState({
      winningSegmentIndex: 0,
      winningSegmentIndexes: [],
      angleCurrent,
      remainingSegmentIndexes: [],
    });
    setWinner("???");
    setWinnerIndex(-1);
  };

  const onStopSpinning = () => {
    setMustSpin(false);
    setWinner(segments[nextIndex]);
    setState({
      winningSegmentIndexes: disableWinners ? [...winningSegmentIndexes, nextIndex] : [],
    });
    setWinnerIndex(nextIndex);

    if (shouldPlaySound) {
      play();
    }
  };

  return (
    <OuterContainer>
      <Wheel
        height={dimens.toString()}
        width={dimens.toString()}
        mustStartSpinning={mustSpin}
        prizeNumber={winningSegmentIndex ?? -1}
        data={data}
        backgroundColors={["yellow"]}
        textColors={["#ffffff"]}
        onStopSpinning={onStopSpinning}
        radiusLineWidth={0}
        outerBorderWidth={2}
        outerBorderColor="#DDE1E8"
        fontSize={1.852950857142857}
        // pointerProps={{ src: SpinnerNotch }}
        // fontFamily="LEMON MILK Pro"
      />
      <Row style={{ gap: "10px" }}>
        <Winner
          backgroundColor={winnerIndex !== -1 ? safeGetColourSegment(winnerIndex) : "lightgray"}
          fontSize={winner !== "" ? `${getFontSize(winner, 6, 10)}cqw` : "10cqw"}
        >
          <p>{winner !== "" ? winner : "???"}</p>
        </Winner>
        <GoButton
          withShadow={false}
          Icon={Spin}
          onClick={spin}
          disabled={
            mustSpin || segments.length <= 1 || winningSegmentIndexes.length === segments.length
          }
        />
        <ControlButton
          withShadow={false}
          Icon={Reset}
          onClick={reset}
          disabled={mustSpin || winner === "" || segments.length < 2}
        />
      </Row>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  height: 100%;
  position: relative;
  padding: 10px;
  align-items: center;
  justify-content: center;
`;

const GoButton = styled(IconButtonInverted)`
  width: 15cqw;
  aspect-ratio: 1;
  min-width: 30px;
`;

const ControlButton = styled(IconButtonInverted)`
  aspect-ratio: 1;
  padding: 3cqw;
  width: 15cqw;
`;

const Winner = styled(Row)<{ backgroundColor: string; fontSize: string }>`
  background-color: ${(props) => props.backgroundColor};
  justify-content: center;
  align-items: center;
  width: 55cqw;
  border-radius: 8px;
  p {
    font-weight: 600;
    font-size: ${(props) => props.fontSize};
    color: black;
    line-height: 1;
  }
`;
